import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';

const API_BASE = `${getApiBaseUrl()}/onboarding`;

// Standard headers for API calls
const getHeaders = (additionalHeaders = {}) => ({
  'Content-Type': 'application/json',
  ...additionalHeaders
});

const useOnboardingSession = () => {
  const [leadId, setLeadId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentFlow, setCurrentFlow] = useState('signup'); // 'signup', 'verified', 'plan_selected', 'payment_pending', 'completed'
  const [leadData, setLeadData] = useState({
    email: '',
    clinic_name: '',
    password: '',
    contact_phone: '',
    timezone: 'Europe/London',
    currency: 'GBP',
    
    // UTM tracking (if available)
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    
    // Additional tracking
    referrer_url: '',
    landing_page: '',
    ip_address: '',
    user_agent: ''
  });
  
  const [planData, setPlanData] = useState({
    plan_id: '',
    billing_period: 'monthly'
  });

  const [billingConfig, setBillingConfig] = useState({
    freeModeEnabled: false,
    freeModeMessage: '',
    freeModePlanId: '',
    freeModeTrialDays: 14
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Get client tracking information
  const getTrackingInfo = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_content: urlParams.get('utm_content') || '',
      utm_term: urlParams.get('utm_term') || '',
      referrer_url: document.referrer || '',
      landing_page: window.location.href,
      ip_address: '', // Will be set by backend
      user_agent: navigator.userAgent
    };
  }, []);

  // Update lead data
  const updateLeadData = useCallback((updates) => {
    setLeadData(prev => ({ ...prev, ...updates }));
  }, []);

  // Update plan data
  const updatePlanData = useCallback((updates) => {
    setPlanData(prev => ({ ...prev, ...updates }));
  }, []);

  const fetchBillingConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/environment-status`, {
        method: 'GET',
        headers: getHeaders()
      });

      const result = await response.json();

      if (response.ok && result) {
        const freeMode = result.free_mode || {};
        setBillingConfig({
          freeModeEnabled: Boolean(freeMode.enabled),
          freeModeMessage: freeMode.message || '',
          freeModePlanId: freeMode.plan_id || 'free',
          freeModeTrialDays: freeMode.trial_days || 14
        });
      } else {
        console.error('Failed to load billing config, response:', result);
      }
    } catch (error) {
      console.error('Failed to load billing config:', error);
    }
  }, []);

  useEffect(() => {
    fetchBillingConfig();
  }, [fetchBillingConfig]);

  // 1. Create signup lead (replaces old flow step 1)
  const createSignupLead = useCallback(async (formData) => {
    console.log('Creating signup lead with:', formData);
    
    try {
      const trackingInfo = getTrackingInfo();
      const requestData = {
        email: formData.email,
        clinic_name: formData.clinic_name,
        password: formData.password,
        contact_phone: formData.contact_phone || '',
        timezone: formData.timezone || 'Europe/London',
        currency: formData.currency || 'GBP',
        ...trackingInfo
      };

      const response = await fetch(`${API_BASE}/signup-lead`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(requestData)
      });

      const result = await response.json();
      console.log('Signup lead response:', result);
      
      if (!response.ok) {
        return {
          success: false,
          error: result.detail || 'Failed to create signup lead'
        };
      }

      if (result.success) {
        setLeadId(result.lead_id);
        setCurrentFlow(result.action); // 'verification_sent', 'verification_resent', 'redirect_to_plans', etc.
        return result;
      }

      return {
        success: false,
        error: result.message || 'Failed to create signup lead'
      };
    } catch (error) {
      console.error('Create signup lead error:', error);
      return {
        success: false,
        error: 'Unable to create signup lead. Please check your connection and try again.'
      };
    }
  }, [getTrackingInfo]);

  // 2. Verify email with token
  const verifyEmail = useCallback(async (verificationToken) => {
    console.log('Verifying email with token:', verificationToken);
    
    try {
      const response = await fetch(`${API_BASE}/verify-email`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          verification_token: verificationToken
        })
      });

      const result = await response.json();
      console.log('Email verification response:', result);
      
      if (!response.ok) {
        return {
          success: false,
          error: result.detail || 'Email verification failed'
        };
      }

      if (result.success) {
        setLeadId(result.lead_id);
        setCurrentFlow('verified');
        // Update lead data with verified info
        setLeadData(prev => ({
          ...prev,
          email: result.email,
          clinic_name: result.clinic_name
        }));
        return result;
      }

      return {
        success: false,
        error: result.message || 'Email verification failed'
      };
    } catch (error) {
      console.error('Email verification error:', error);
      return {
        success: false,
        error: 'Unable to verify email. Please check your connection and try again.'
      };
    }
  }, []);

  // 2.5. Configure Pabau API key for verified lead
  const configurePabauApiKey = useCallback(async (pabauConfig) => {
    console.log('Configuring Pabau API key for lead:', leadId, 'config:', pabauConfig);
    
    if (!leadId) {
      return {
        success: false,
        error: 'No lead ID found. Please start the signup process again.'
      };
    }
    
    try {
      const response = await fetch(`${API_BASE}/configure-pabau/${leadId}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(pabauConfig)
      });

      const result = await response.json();
      console.log('Pabau configuration response:', result);
      
      if (!response.ok) {
        return {
          success: false,
          error: result.detail || 'Pabau configuration failed'
        };
      }

      if (result.success) {
        setCurrentFlow('pabau_configured');
        return result;
      }

      return {
        success: false,
        error: result.message || 'Pabau configuration failed'
      };
    } catch (error) {
      console.error('Pabau configuration error:', error);
      return {
        success: false,
        error: 'Unable to configure Pabau API. Please check your connection and try again.'
      };
    }
  }, [leadId]);

  // 3. Select plan for verified lead
  const selectPlan = useCallback(async (planSelection) => {
    console.log('Selecting plan for lead:', leadId, 'plan:', planSelection);
    
    if (!leadId) {
      return {
        success: false,
        error: 'No lead ID found. Please start the signup process again.'
      };
    }
    
    try {
      const response = await fetch(`${API_BASE}/select-plan/${leadId}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(planSelection)
      });

      const result = await response.json();
      console.log('Plan selection response:', result);
      
      if (!response.ok) {
        return {
          success: false,
          error: result.detail || 'Plan selection failed'
        };
      }

      if (result.success) {
        setPlanData(planSelection);
        setCurrentFlow('plan_selected');
        return result;
      }

      return {
        success: false,
        error: result.message || 'Plan selection failed'
      };
    } catch (error) {
      console.error('Plan selection error:', error);
      return {
        success: false,
        error: 'Unable to select plan. Please check your connection and try again.'
      };
    }
  }, [leadId]);

  // 4. Create Stripe checkout session
  const createCheckoutSession = useCallback(async () => {
    console.log('Creating checkout session for lead:', leadId);
    
    if (!leadId) {
      return {
        success: false,
        error: 'No lead ID found. Please start the signup process again.'
      };
    }
    
    try {
      const response = await fetch(`${API_BASE}/create-checkout/${leadId}`, {
        method: 'POST',
        headers: getHeaders()
      });

      const result = await response.json();
      console.log('Checkout session response:', result);
      
      if (!response.ok) {
        return {
          success: false,
          error: result.detail || 'Failed to create checkout session'
        };
      }

      if (result.success && result.checkout_url) {
        setCurrentFlow('payment_pending');
        
        // Redirect to Stripe checkout
        window.location.href = result.checkout_url;
        
        return {
          success: true,
          redirecting: true,
          message: 'Redirecting to payment setup...'
        };
      }

      return {
        success: false,
        error: result.message || 'Failed to create checkout session'
      };
    } catch (error) {
      console.error('Create checkout session error:', error);
      return {
        success: false,
        error: 'Unable to create checkout session. Please check your connection and try again.'
      };
    }
  }, [leadId]);

  // 5. Get lead information
  const getLeadInfo = useCallback(async (leadIdParam = null) => {
    const targetLeadId = leadIdParam || leadId;
    
    if (!targetLeadId) return null;

    try {
      const response = await fetch(`${API_BASE}/lead/${targetLeadId}`, {
        method: 'GET',
        headers: getHeaders()
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('Lead info fetch error:', result);
        return null;
      }

      // Update local state with lead info
      if (result.id === leadId) {
        setLeadData(prev => ({
          ...prev,
          email: result.email,
          clinic_name: result.clinic_name
        }));
        
        if (result.selected_plan) {
          setPlanData({
            plan_id: result.selected_plan,
            billing_period: result.billing_period
          });
        }
        
        // Update flow state based on lead status
        setCurrentFlow(result.status);
      }

      return result;
    } catch (error) {
      console.error('Lead info fetch error:', error);
      return null;
    }
  }, [leadId]);

  // Legacy validation function (simplified for new flow)
  const validateStep = useCallback(async (stepNumber, data) => {
    console.log('validateStep called for step:', stepNumber, 'data:', data);
    
    // Simple client-side validation for new flow
    if (stepNumber === 1) {
      const errors = {};
      
      if (!data.email || !data.email.includes('@')) {
        errors.email = 'Please enter a valid email address';
      }
      
      if (!data.clinic_name || data.clinic_name.length < 2) {
        errors.clinic_name = 'Please enter a clinic name (at least 2 characters)';
      }
      
      if (!data.password || data.password.length < 8) {
        errors.password = 'Password must be at least 8 characters long';
      }
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return {
          valid: false,
          errors: errors,
          error: 'Please fix the validation errors'
        };
      }
    }
    
    setValidationErrors({});
    return { valid: true };
  }, []);

  // Initialize from URL parameters (for email verification links)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const leadIdParam = urlParams.get('lead_id');
    
    if (token) {
      // This is an email verification link
      verifyEmail(token);
    } else if (leadIdParam) {
      // This is a return to existing lead
      setLeadId(leadIdParam);
      getLeadInfo(leadIdParam);
    }
  }, [verifyEmail, getLeadInfo]);

  return {
    // Enhanced state
    leadId,
    sessionId,
    currentFlow,
    leadData,
    planData,
    billingConfig,
    validationErrors,
    
    // Enhanced actions
    updateLeadData,
    updatePlanData,
    createSignupLead,
    verifyEmail,
    configurePabauApiKey,
    selectPlan,
    createCheckoutSession,
    getLeadInfo,
    refreshBillingConfig: fetchBillingConfig,
    
    // Legacy compatibility (simplified)
    validateStep,
    sessionData: { ...leadData, subscription: planData }, // For backward compatibility
    updateSessionData: updateLeadData // For backward compatibility
  };
};

export { useOnboardingSession }; 
